import { Injectable, NgZone, inject } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import { type Schema } from '../../../../amplify/data/resource';
import { getCurrentUser } from 'aws-amplify/auth';
import { BehaviorSubject } from 'rxjs';
import { isTauri } from '@tauri-apps/api/core';
import { sendNotification, isPermissionGranted, requestPermission } from '@tauri-apps/plugin-notification';
import { getCurrentWindow, UserAttentionType } from '@tauri-apps/api/window';
import { Notification, Toast, ToastType } from '../models/notifications-toast.model';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private _client?: ReturnType<typeof generateClient<Schema>>;

  private get client() {
    if (!this._client) {
      this._client = generateClient<Schema>();
    }
    return this._client;
  }

  private ngZone = inject(NgZone);
  private userService = inject(UserService);

  public notifications$ = new BehaviorSubject<Notification[]>([]);
  public unreadCount$ = new BehaviorSubject<number>(0);
  private lastNotificationId: string | null = null;
  private currentCognitoId: string | null = null;
  private toastId = 0;
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  public toasts$ = this.toastsSubject.asObservable();

  constructor() {
    // Don't subscribe in constructor - will be called after authentication
  }

  async init() {
    await this.initTauriPermissions();
    this.subscribeToNotifications();
  }

  showToast(message: string, type: ToastType = 'info', duration = 3000) {
    const toast: Toast = {id: ++this.toastId, message, type };
    this.toastsSubject.next([...this.toastsSubject.value, toast]);
    setTimeout(() => this.removeToast(toast.id), duration);
  }

  removeToast(id: number) {
    this.toastsSubject.next(
      this.toastsSubject.value.filter(t => t.id !== id)
    );
  }

  async initTauriPermissions() {
    if (!isTauri()) return;
    let permissionGranted = await isPermissionGranted();
    if (!permissionGranted) {
      const permission = await requestPermission();
      permissionGranted = permission === 'granted';
    }
  }

  async subscribeToNotifications() {
    try {
      const authUser = await getCurrentUser();
      this.currentCognitoId = authUser.userId;

      this.client.models.Notification.observeQuery().subscribe({
        next: async ({ items, isSynced }) => {
          this.ngZone.run(async () => {
            // Filter to only show notifications for the current user
            const myNotifications = items.filter(n => n.recipientId === this.currentCognitoId);
            const sorted = myNotifications.sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
            const latest = sorted[0];
            if (isSynced && latest && latest.id !== this.lastNotificationId) {
              this.lastNotificationId = latest.id;
              if (!latest.isRead) {
                await this.triggerDesktopNotification(
                  latest.senderName || 'Système',
                  latest.content
                );
              }
            } else if (!isSynced && latest) this.lastNotificationId = latest.id;
            this.notifications$.next(sorted);
            this.unreadCount$.next(sorted.filter(n => !n.isRead).length);
          });
        },
        error: err => console.error('Subscription error', err)
      });
    } catch {
      console.log('User not logged in yet');
    }
  }

  async triggerDesktopNotification(title: string, body: string) {
    if (!isTauri()) return;
    if (await isPermissionGranted()) {
      sendNotification({
        title: `New notification of ${title}`,
        body
      });
    }
    await getCurrentWindow().requestUserAttention(UserAttentionType.Critical);
  }

  async sendNotification(
    recipientId: string,
    content: string,
    opts: { taskId?: string; todoId?: string; projectId?: string; senderName?: string } = {}
  ) {
    await this.client.models.Notification.create({
      recipientId,
      content,
      taskId: opts.taskId ?? undefined,
      todoId: opts.todoId ?? undefined,
      projectId: opts.projectId ?? undefined,
      senderName: opts.senderName ?? undefined,
      isRead: false,
    });
  }

  // ── ID resolution helper ──────────────────────────────────────────────────

  /**
   * Resolve a database user ID to a cognitoId for notification delivery.
   * Returns undefined if the user can't be found.
   */
  private resolveCognitoId(databaseUserId: string): string | undefined {
    const users = this.userService.getUsersSnapshot();
    const user = users.find(u => u.id === databaseUserId);
    return user?.cognitoId ?? undefined;
  }

  // ── Assignment notification helpers ────────────────────────────────────────

  /**
   * Notify users when they are assigned to a task.
   * Only notifies *newly* assigned users (not previously assigned) and
   * never notifies the person who made the assignment.
   */
  async notifyTaskAssignment(opts: {
    taskName: string;
    newEmployeeIds: string[];
    previousEmployeeIds: string[];
    currentUserId: string;
    senderName: string;
    taskId?: string;
    projectId?: string;
  }) {
    const added = opts.newEmployeeIds.filter(
      id => !opts.previousEmployeeIds.includes(id) && id !== opts.currentUserId
    );
    if (!added.length) return;
    const content = `You have been assigned to the task "${opts.taskName}"`;
    await Promise.all(
      added.map(uid => {
        const cognitoId = this.resolveCognitoId(uid);
        if (!cognitoId) {
          console.warn(`[NotificationService] Cannot resolve cognitoId for user ${uid}`);
          return Promise.resolve();
        }
        return this.sendNotification(cognitoId, content, {
          taskId: opts.taskId,
          projectId: opts.projectId,
          senderName: opts.senderName,
        });
      })
    );
  }

  /**
   * Notify users when they are assigned to a todo.
   * Only notifies *newly* assigned users and never the sender.
   */
  async notifyTodoAssignment(opts: {
    todoName: string;
    newUserIds: string[];
    previousUserIds: string[];
    currentUserId: string;
    senderName: string;
    todoId?: string;
  }) {
    const added = opts.newUserIds.filter(
      id => !opts.previousUserIds.includes(id) && id !== opts.currentUserId
    );
    if (!added.length) return;
    const content = `You have been assigned to the todo "${opts.todoName}"`;
    await Promise.all(
      added.map(uid => {
        const cognitoId = this.resolveCognitoId(uid);
        if (!cognitoId) {
          console.warn(`[NotificationService] Cannot resolve cognitoId for user ${uid}`);
          return Promise.resolve();
        }
        return this.sendNotification(cognitoId, content, {
          todoId: opts.todoId,
          senderName: opts.senderName,
        });
      })
    );
  }

  async markAsRead(notificationId: string) {
    await this.client.models.Notification.update({ id: notificationId, isRead: true });
  }
}
